import {
  PROXY,
  YES_NO,
  applicantSection,
  feeInfo,
  feeProofAttachment,
  otherAttachments,
  parcelsField,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import {
  EbiCondition,
  EEbiFormCategory,
  EEbiFormOffice,
  IEbiField,
  IEbiFormAttachment,
  IEbiFormDefinition,
} from "modules/ebiForms/typings";

/**
 * Wspólne buildery pakietu „Drogi” (docs/magistrat/ebi-wnioski: B5, B6, D1, D2, E8, E9) — ta sama procedura u zarządcy
 * drogi gminnej (urząd gminy albo zarząd dróg) i powiatowej (PZD); definicje różnią się kodem, organem, częścią katalogu
 * (`office`) i kategorią drogi.
 *
 * Zajęcie pasa drogowego (art. 40 udp): pola z § 1 ust. 2 rozporządzenia RM z 1.06.2004 r. (t.j. Dz.U. 2016 poz. 1264,
 * tekst https://api.sejm.gov.pl/eli/acts/DU/2016/1264/text.html — pobrany 2026-10-22: tak), załączniki z § 1 ust. 3, 5 i 6;
 * cele zajęcia z art. 40 ust. 2 udp (tekst Dz.U. 2025 poz. 889 — pobrany: tak). Podział powierzchni na elementy pasa
 * i dane wykonawcy jak we wzorach Chodzieży (DOCX), Siedlec (PDF) i ZDMK Kraków (ZDMK-24, PDF) — pobrane: tak.
 * Zjazd (art. 29 udp): brak wzoru ogólnopolskiego; pola jak we wzorach Bierunia (XX GK), Nysy i PZD Tarnów — pobrane: tak.
 *
 * Do potwierdzenia z wydziałem: elementy pasa drogowego (zależą od podziału stawek w uchwale rady), lista załączników
 * „praktyki urzędów” (nieobowiązkowe pozycje z podpowiedzią). Kalkulatora opłaty za zajęcie pasa nie ma — stawki są
 * w uchwale każdej rady, silnik liczy tylko stawkę × liczbę sztuk.
 */

/** Kategoria drogi publicznej, której zarządca załatwia sprawę. */
export type EbiRoadCategory = "gminna" | "powiatowa";

/** Organ w poszczególnych częściach katalogu. */
export const ROAD_ORGAN = {
  municipality: "Wójt / Burmistrz / Prezydent Miasta jako zarządca drogi gminnej",
  municipalUnit: "Zarząd dróg (jednostka organizacyjna gminy) w imieniu wójta / burmistrza / prezydenta miasta — zarządcy drogi gminnej",
  countyUnit: "Zarząd Powiatu jako zarządca drogi powiatowej (w praktyce powiatowy zarząd dróg)",
} as const;

const ROAD: Record<EbiRoadCategory, { genitive: string; keyword: string; council: string; numberLabel: string }> = {
  gminna: {
    genitive: "drogi gminnej",
    keyword: "droga gminna",
    council: "rady gminy",
    numberLabel: "Droga gminna — numer drogi albo nazwa ulicy",
  },
  powiatowa: {
    genitive: "drogi powiatowej",
    keyword: "droga powiatowa",
    council: "rady powiatu",
    numberLabel: "Numer drogi powiatowej",
  },
};

export interface IEbiRoadFormOptions {
  code: string;
  title: string;
  description: string;
  organ: string;
  office: EEbiFormOffice;
  road: EbiRoadCategory;
}

/** Droga i miejscowość (przy drodze powiatowej także gmina) — kody z sufiksem, bo miejscowość wnioskodawcy już jest. */
const roadPlaceFields = (road: EbiRoadCategory, suffix: string): IEbiField[] => [
  { code: "drogaNumer", type: "text", label: ROAD[road].numberLabel, required: true, width: "half", maxLength: 200 },
  { code: `miejscowosc${suffix}`, type: "text", label: "Miejscowość", required: true, width: "half", maxLength: 100 },
  ...(road === "powiatowa"
    ? [{ code: `gmina${suffix}`, type: "text", label: "Gmina", required: true, width: "half", maxLength: 100 } satisfies IEbiField]
    : []),
];

/** Elementy pasa drogowego do wyboru, gdy urząd nie skonfigurował własnego podziału (jak we wzorze Chodzieży). */
const LANE_ELEMENTS = [
  { value: "jezdnia20", label: "jezdnia — do 20% szerokości" },
  { value: "jezdnia50", label: "jezdnia — powyżej 20% do 50% szerokości" },
  { value: "jezdniaPow50", label: "jezdnia — powyżej 50% szerokości" },
  { value: "chodnik", label: "chodnik, zatoka, plac, parking" },
  { value: "inne", label: "inne elementy pasa drogowego" },
];

// ── zajęcie pasa drogowego (B5, D1, E8) ──────────────────────────────────────

const PURPOSE = "celZajecia";
const WORKS: EbiCondition = { field: PURPOSE, equals: "roboty" };
const AREA_BASED: EbiCondition = { field: PURPOSE, in: ["roboty", "wylacznosc"] };
const DEVICE: EbiCondition = { field: PURPOSE, in: ["urzadzenieLiniowe", "urzadzenieInne"] };
const LINEAR: EbiCondition = { field: PURPOSE, equals: "urzadzenieLiniowe" };
const ADVERT: EbiCondition = { field: PURPOSE, equals: "reklama" };
const PLACEMENT: EbiCondition = { field: PURPOSE, in: ["urzadzenieLiniowe", "urzadzenieInne", "reklama"] };
const WORKS_OR_DEVICE: EbiCondition = { field: PURPOSE, in: ["roboty", "urzadzenieLiniowe", "urzadzenieInne"] };
const WORKS_OR_PLACEMENT: EbiCondition = { field: PURPOSE, in: ["roboty", "urzadzenieLiniowe", "urzadzenieInne", "reklama"] };
const OBJECT_OR_ADVERT: EbiCondition = { field: PURPOSE, in: ["urzadzenieInne", "reklama"] };
const AFFECTS_TRAFFIC: EbiCondition = { field: "wplywNaRuch", equals: "tak" };

const AREA_FIELD = (code: string, label: string): IEbiField => ({
  code,
  type: "number",
  label,
  required: true,
  decimals: 2,
  min: 0.01,
  width: "third",
});

/** Wniosek o zezwolenie na zajęcie pasa drogowego (art. 40 udp, § 1 rozporządzenia RM z 1.06.2004 r.). */
export const laneOccupationForm = (o: IEbiRoadFormOptions): IEbiFormDefinition => {
  const road = ROAD[o.road];

  return {
    code: o.code,
    version: 1,
    title: o.title,
    category: EEbiFormCategory.Roads,
    office: o.office,
    description: o.description,
    keywords: ["pas drogowy", "zajęcie pasa drogowego", "roboty w pasie drogowym", "urządzenie obce", "reklama", road.keyword, "zezwolenie"],
    organ: o.organ,
    legalBasis:
      "art. 40 ust. 1–4 i 11 ustawy z dnia 21 marca 1985 r. o drogach publicznych; § 1 rozporządzenia Rady Ministrów z dnia 1 czerwca 2004 r. w sprawie określenia warunków udzielania zezwoleń na zajęcie pasa drogowego",
    request: `Wnoszę o wydanie zezwolenia na zajęcie pasa drogowego ${road.genitive} w celu, w miejscu i w okresie wskazanych poniżej.`,
    feeLabel: "wg stawek",
    fee: `Zezwolenie jest zwolnione z opłaty skarbowej; pełnomocnictwo — 17 zł. Opłatę za zajęcie pasa drogowego ustala decyzja według stawek z uchwały ${road.council}.`,
    deadlineLabel: "do miesiąca",
    deadline:
      "Do miesiąca, w sprawach szczególnie skomplikowanych do 2 miesięcy (art. 35 KPA); dla infrastruktury telekomunikacyjnej do 30 dni (art. 40 ust. 2b ustawy o drogach publicznych).",
    sections: [
      applicantSection(),
      representationSection(),
      {
        code: "cel",
        title: "Cel zajęcia pasa drogowego",
        fields: [
          {
            code: PURPOSE,
            type: "radio",
            label: "Zajęcie pasa drogowego w celu (art. 40 ust. 2 ustawy o drogach publicznych)",
            required: true,
            options: [
              { value: "roboty", label: "prowadzenia robót w pasie drogowym" },
              {
                value: "urzadzenieLiniowe",
                label:
                  "umieszczenia w pasie drogowym liniowego urządzenia obcego (np. sieci wodociągowej, kanalizacyjnej, gazowej, ciepłowniczej, elektroenergetycznej, telekomunikacyjnej)",
              },
              { value: "urzadzenieInne", label: "umieszczenia w pasie drogowym innego urządzenia obcego (np. kiosku, obiektu handlowego)" },
              { value: "reklama", label: "umieszczenia w pasie drogowym reklamy" },
              { value: "wylacznosc", label: "zajęcia pasa drogowego na prawach wyłączności w innym celu" },
            ],
          },
          {
            code: "opisZajecia",
            type: "textarea",
            label: "Opis zamierzenia — rodzaj robót albo nazwa i rodzaj urządzenia, obiektu lub reklamy oraz ich wymiary",
            required: true,
            maxLength: 3000,
          },
          {
            code: "infrastrukturaTelekom",
            type: "radio",
            label: "Czy obiekt lub urządzenie stanowi element infrastruktury telekomunikacyjnej?",
            required: true,
            visibleWhen: WORKS_OR_DEVICE,
            options: YES_NO,
            hint: "Dla infrastruktury telekomunikacyjnej obowiązują niższe stawki opłaty (art. 40 ust. 8 ustawy o drogach publicznych).",
          },
          {
            code: "decyzjaLokalizacyjnaZnak",
            type: "text",
            label: "Znak decyzji zezwalającej na lokalizację urządzenia obcego lub reklamy w pasie drogowym (art. 39 ust. 3)",
            visibleWhen: WORKS_OR_PLACEMENT,
            width: "twoThirds",
            maxLength: 100,
            hint: "Jeśli została wydana — zarządca drogi wydaje ją na osobny wniosek, zanim urządzenie lub reklama zostanie umieszczona.",
          },
          { code: "decyzjaLokalizacyjnaData", type: "date", label: "Data decyzji", visibleWhen: WORKS_OR_PLACEMENT, width: "third" },
        ],
      },
      {
        code: "lokalizacja",
        title: "Lokalizacja zajęcia",
        fields: [
          ...roadPlaceFields(o.road, "Zajecia"),
          {
            code: "odcinekDrogi",
            type: "text",
            label: "Odcinek drogi (od km do km albo między punktami charakterystycznymi) i strona drogi",
            maxLength: 300,
          },
          {
            code: "dzialkiDrogowe",
            type: "text",
            label: "Działki ewidencyjne pasa drogowego — numery i obręb (jeśli znasz)",
            maxLength: 300,
            placeholder: "np. 125/4, 126 — obręb 0003",
          },
        ],
      },
      {
        code: "powierzchnia",
        title: "Powierzchnia zajęcia pasa drogowego",
        description:
          "Teren zajęty obejmuje cały plac budowy w pasie drogowym: miejsce wykopu, odkładu urobku i składowania materiałów, powierzchnię zajętą przez sprzęt i barakowozy oraz drogi objazdowe (z wyjątkiem objazdu po istniejącej sieci dróg).",
        visibleWhen: AREA_BASED,
        fields: [
          {
            code: "elementyPasa",
            type: "repeat",
            label: "Zajmowane elementy pasa drogowego",
            itemLabel: "Element pasa",
            required: true,
            min: 1,
            max: 20,
            fields: [
              { code: "element", type: "select", label: "Element pasa drogowego", required: true, options: LANE_ELEMENTS },
              AREA_FIELD("dlugosc", "Długość [m]"),
              AREA_FIELD("szerokosc", "Szerokość [m]"),
              AREA_FIELD("powierzchnia", "Powierzchnia [m²]"),
            ],
          },
        ],
      },
      {
        code: "przedmiot",
        title: "Umieszczane urządzenie lub reklama",
        visibleWhen: PLACEMENT,
        fields: [
          {
            code: "powierzchniaRzutu",
            type: "number",
            label: "Powierzchnia pasa drogowego zajęta przez rzut poziomy urządzenia [m²]",
            required: true,
            visibleWhen: DEVICE,
            decimals: 2,
            min: 0.01,
            width: "half",
          },
          {
            code: "dlugoscUrzadzenia",
            type: "number",
            label: "Długość urządzenia w pasie drogowym [m]",
            visibleWhen: LINEAR,
            decimals: 2,
            min: 0.01,
            width: "half",
          },
          {
            code: "usytuowanieUrzadzenia",
            type: "checkboxes",
            label: "Usytuowanie urządzenia",
            required: true,
            visibleWhen: DEVICE,
            options: [
              { value: "wJezdni", label: "w jezdni" },
              { value: "pozaJezdnia", label: "poza jezdnią" },
              { value: "obiektInzynierski", label: "na drogowym obiekcie inżynierskim (np. moście)" },
            ],
          },
          {
            code: "przebiegUrzadzenia",
            type: "checkboxes",
            label: "Przebieg urządzenia",
            visibleWhen: LINEAR,
            options: [
              { value: "wzdluz", label: "wzdłuż drogi" },
              { value: "wPoprzek", label: "w poprzek drogi" },
            ],
          },
          {
            code: "powierzchniaReklamy",
            type: "number",
            label: "Powierzchnia reklamy [m²]",
            required: true,
            visibleWhen: ADVERT,
            decimals: 2,
            min: 0.01,
            width: "half",
          },
        ],
      },
      {
        code: "okres",
        title: "Planowany okres zajęcia pasa drogowego",
        fields: [
          { code: "dataOd", type: "date", label: "Od dnia", required: true, width: "half" },
          {
            code: "dataDo",
            type: "date",
            label: "Do dnia",
            required: true,
            width: "half",
            hint: "Przy robotach — dzień przywrócenia terenu do stanu pierwotnego i zgłoszenia go do odbioru.",
          },
          { code: "etapowanie", type: "checkbox", label: "Roboty będą prowadzone etapami — dołączę harmonogram robót", visibleWhen: WORKS },
          feeInfo(
            "infoTermin",
            "Wniosek złóż z wyprzedzeniem — zarządcy dróg zalecają co najmniej 30 dni przed planowanym zajęciem. Wejść w pas drogowy można dopiero na podstawie zezwolenia. Za zajęcie bez zezwolenia, dłuższe albo na większej powierzchni niż w zezwoleniu zarządca wymierza karę w wysokości 10-krotności opłaty (art. 40 ust. 12 ustawy o drogach publicznych).",
          ),
        ],
      },
      {
        code: "ruch",
        title: "Ruch drogowy i prowadzenie robót",
        fields: [
          {
            code: "wplywNaRuch",
            type: "radio",
            label:
              "Czy zajęcie pasa drogowego wpływa na ruch drogowy, ogranicza widoczność na drodze albo powoduje zmiany w istniejącej organizacji ruchu pojazdów lub pieszych?",
            required: true,
            options: YES_NO,
            hint: "Jeśli tak — dołącz zatwierdzony projekt organizacji ruchu.",
          },
          {
            code: "zabezpieczenieRobot",
            type: "textarea",
            label: "Sposób zabezpieczenia robót",
            visibleWhen: WORKS,
            requiredWhen: { field: "wplywNaRuch", equals: "nie" },
            maxLength: 2000,
            hint: "Wymagany, gdy nie jest wymagany projekt organizacji ruchu (§ 1 ust. 5 pkt 1 rozporządzenia).",
          },
          {
            code: "oswiadczenieBudowlane",
            type: "radio",
            label: "Oświadczenie dotyczące robót (§ 1 ust. 5 pkt 2 rozporządzenia)",
            required: true,
            visibleWhen: WORKS,
            options: [
              { value: "pozwolenie", label: "inwestor posiada ważne pozwolenie na budowę obiektu umieszczanego w pasie drogowym" },
              { value: "zgloszenie", label: "budowa lub prowadzone roboty zostały zgłoszone właściwemu organowi administracji architektoniczno-budowlanej" },
              {
                value: "przylacza",
                label:
                  "inwestor zamierza budować przyłącza elektroenergetyczne, wodociągowe, kanalizacyjne, gazowe, cieplne lub telekomunikacyjne, dla których plan sytuacyjny sporządzono na kopii aktualnej mapy zasadniczej lub mapy jednostkowej przyjętej do państwowego zasobu geodezyjnego i kartograficznego",
              },
            ],
          },
          {
            code: "wykonawca",
            type: "text",
            label: "Wykonawca robót — nazwa albo imię i nazwisko, adres, telefon",
            visibleWhen: WORKS,
            maxLength: 300,
            hint: "Nieobowiązkowo.",
          },
          { code: "kierownikRobot", type: "text", label: "Kierownik robót — imię i nazwisko, telefon", visibleWhen: WORKS, maxLength: 200, hint: "Nieobowiązkowo." },
        ],
      },
      {
        code: "oplata",
        title: "Opłata za zajęcie pasa drogowego",
        fields: [
          feeInfo(
            "infoOplata",
            `Zezwolenie jest zwolnione z opłaty skarbowej. Opłatę za zajęcie pasa drogowego ustala zarządca drogi w decyzji według stawek z uchwały ${road.council}: przy robotach, zajęciu na prawach wyłączności, innych urządzeniach i reklamach — powierzchnia × stawka dzienna × liczba dni, przy urządzeniach liniowych — powierzchnia rzutu poziomego × stawka roczna (art. 40 ust. 4–8 ustawy o drogach publicznych). Zajęcie mniej niż 1 m² liczy się jak 1 m², a krócej niż 24 godziny — jak 1 dzień. Opłatę wnosi się w ciągu 14 dni od dnia, w którym decyzja stała się ostateczna.`,
          ),
        ],
      },
      replySection("Sposób odbioru decyzji"),
    ],
    attachments: [
      {
        code: "planSytuacyjny",
        label: "Szczegółowy plan sytuacyjny w skali 1:1000 lub 1:500 z zaznaczeniem granic i wymiarów planowanej powierzchni zajęcia pasa drogowego",
        hint: "Przy reklamie — także z jej wymiarami (§ 1 ust. 3 pkt 1 rozporządzenia).",
        required: true,
      },
      {
        code: "projektOrganizacjiRuchu",
        label: "Zatwierdzony projekt organizacji ruchu",
        hint: "Gdy zajęcie wpływa na ruch drogowy (§ 1 ust. 3 pkt 2 rozporządzenia).",
        visibleWhen: AFFECTS_TRAFFIC,
        required: true,
      },
      {
        code: "planOrientacyjny",
        label: "Ogólny plan orientacyjny w skali 1:10 000 lub 1:25 000 z zaznaczeniem zajmowanego odcinka pasa drogowego",
        hint: "§ 1 ust. 5 pkt 1 rozporządzenia.",
        visibleWhen: WORKS,
        required: true,
      },
      {
        code: "harmonogramRobot",
        label: "Harmonogram robót prowadzonych w pasie drogowym",
        hint: "Dla każdego etapu: zajmowany element pasa drogowego, powierzchnia i okres zajęcia.",
        visibleWhen: { all: [WORKS, { field: "etapowanie", equals: true }] },
        required: true,
      },
      {
        code: "projektBudowlany",
        label: "Projekt budowlany obiektu umieszczanego w pasie drogowym",
        hint: "Nieobowiązkowo — zarządca drogi może go zażądać przed wydaniem zezwolenia (§ 1 ust. 6 rozporządzenia).",
        visibleWhen: WORKS_OR_DEVICE,
      },
      {
        code: "decyzjaLokalizacyjna",
        label: "Decyzja zezwalająca na lokalizację urządzenia obcego lub reklamy w pasie drogowym (art. 39 ust. 3)",
        hint: "Nieobowiązkowo — wystarczą znak i data decyzji podane we wniosku.",
        visibleWhen: WORKS_OR_PLACEMENT,
      },
      {
        code: "rysunekObiektu",
        label: "Rysunek lub wizualizacja urządzenia, obiektu albo reklamy z wymiarami",
        hint: "Nieobowiązkowo — niektórzy zarządcy dróg o to proszą.",
        visibleWhen: OBJECT_OR_ADVERT,
      },
      {
        code: "odpisRejestru",
        label: "Odpis z KRS albo umowa spółki cywilnej mocodawcy",
        hint: "Nieobowiązkowo — gdy mocodawcą jest spółka, a zarządca drogi o to prosi.",
        visibleWhen: PROXY,
      },
      ...proxyAttachments(),
      otherAttachments(),
    ],
  };
};

// ── zjazd (B6, D2, E9) ───────────────────────────────────────────────────────

const RESIDENTIAL: EbiCondition = { field: "przeznaczenieZjazdu", equals: "mieszkaniowy" };
const OTHER_PURPOSE: EbiCondition = { field: "przeznaczenieZjazdu", equals: "inny" };

/** Tytuł prawny do nieruchomości przyległej (oświadczenie we wniosku, jak we wzorach Bierunia, Tarnowa i Nysy). */
export const legalTitleFields = (): IEbiField[] => [
  {
    code: "tytulPrawny",
    type: "radio",
    label: "Tytuł prawny wnioskodawcy (mocodawcy) do nieruchomości",
    required: true,
    options: [
      { value: "wlasciciel", label: "właściciel" },
      { value: "wspolwlasciciel", label: "współwłaściciel" },
      { value: "uzytkownikWieczysty", label: "użytkownik wieczysty" },
      { value: "zarzadca", label: "zarządca" },
      { value: "dzierzawca", label: "dzierżawca" },
      { value: "inny", label: "inny tytuł prawny" },
    ],
  },
  { code: "tytulPrawnyInny", type: "text", label: "Jaki tytuł prawny", required: true, visibleWhen: { field: "tytulPrawny", equals: "inny" }, maxLength: 200 },
];

/** Wniosek o zezwolenie na lokalizację albo przebudowę zjazdu (art. 29 udp). */
export const drivewayForm = (o: IEbiRoadFormOptions): IEbiFormDefinition => {
  const road = ROAD[o.road];

  const roadRegisterExtract: IEbiFormAttachment[] =
    o.road === "gminna"
      ? [{ code: "wypisEwidencjiDrogi", label: "Wypis z ewidencji gruntów dla działek drogowych objętych wnioskiem", hint: "Nieobowiązkowo — tylko jeśli urząd tego wymaga." }]
      : [];

  return {
    code: o.code,
    version: 1,
    title: o.title,
    category: EEbiFormCategory.Roads,
    office: o.office,
    description: o.description,
    keywords: ["zjazd", "wjazd", "lokalizacja zjazdu", "przebudowa zjazdu", road.keyword, "zezwolenie"],
    organ: o.organ,
    legalBasis:
      "art. 29 ust. 1–4 ustawy z dnia 21 marca 1985 r. o drogach publicznych; ustawa z dnia 16 listopada 2006 r. o opłacie skarbowej (część III ust. 44 pkt 2 załącznika, zwolnienie — art. 2 ust. 1 pkt 2)",
    request: `Na podstawie art. 29 ustawy z dnia 21 marca 1985 r. o drogach publicznych wnoszę o wydanie zezwolenia na lokalizację albo przebudowę zjazdu z ${road.genitive} do nieruchomości wskazanej poniżej.`,
    feeLabel: "82 zł",
    fee: "82 zł opłaty skarbowej; zjazd służący budownictwu mieszkaniowemu (do budynku mieszkalnego) jest zwolniony. Pełnomocnictwo — 17 zł.",
    deadlineLabel: "do miesiąca",
    deadline: "Do miesiąca, w sprawach szczególnie skomplikowanych do 2 miesięcy (art. 35 KPA).",
    sections: [
      applicantSection(),
      representationSection(),
      {
        code: "zjazd",
        title: "Zjazd",
        fields: [
          {
            code: "trybZjazdu",
            type: "radio",
            label: "Wnoszę o zezwolenie na",
            required: true,
            options: [
              { value: "lokalizacja", label: "lokalizację nowego zjazdu" },
              { value: "przebudowa", label: "przebudowę istniejącego zjazdu" },
            ],
          },
          {
            code: "rodzajZjazdu",
            type: "radio",
            label: "Rodzaj zjazdu",
            required: true,
            defaultValue: "zwykly",
            options: [
              { value: "zwykly", label: "zwykły" },
              { value: "techniczny", label: "techniczny" },
              { value: "awaryjny", label: "awaryjny" },
            ],
          },
          {
            code: "przeznaczenieZjazdu",
            type: "radio",
            label: "Zjazd ma służyć",
            required: true,
            options: [
              { value: "mieszkaniowy", label: "obsłudze budynku mieszkalnego (budownictwo mieszkaniowe) — bez opłaty skarbowej" },
              {
                value: "inny",
                label: "obsłudze nieruchomości wykorzystywanej na inne cele (np. działalność gospodarcza, obiekt użyteczności publicznej, pole uprawne) — opłata skarbowa 82 zł",
              },
            ],
          },
          ...roadPlaceFields(o.road, "Zjazdu"),
        ],
      },
      {
        code: "nieruchomosc",
        title: "Nieruchomość, do której prowadzi zjazd",
        fields: [
          parcelsField(),
          ...legalTitleFields(),
          {
            code: "numerKw",
            type: "text",
            label: "Numer księgi wieczystej nieruchomości",
            maxLength: 30,
            placeholder: "np. LU1I/00012345/6",
            hint: "Jeśli podasz numer księgi wieczystej, kopia dokumentu potwierdzającego tytuł prawny jest nieobowiązkowa.",
          },
          { code: "wykorzystanieNieruchomosci", type: "textarea", label: "Nieruchomość jest wykorzystywana na cele", required: true, maxLength: 1000 },
          {
            code: "zmianaWykorzystania",
            type: "radio",
            label: "Po wybudowaniu lub przebudowie zjazdu sposób wykorzystania nieruchomości",
            required: true,
            options: [
              { value: "nie", label: "nie ulegnie zmianie" },
              { value: "tak", label: "ulegnie zmianie" },
            ],
          },
          {
            code: "opisZmiany",
            type: "textarea",
            label: "Na czym polega zmiana sposobu wykorzystania nieruchomości",
            required: true,
            visibleWhen: { field: "zmianaWykorzystania", equals: "tak" },
            maxLength: 1000,
          },
          {
            code: "pojazdyRuch",
            type: "textarea",
            label: "Rodzaj pojazdów, które będą korzystać ze zjazdu, i przewidywane natężenie ruchu",
            requiredWhen: OTHER_PURPOSE,
            maxLength: 1000,
            hint: "Np. samochód osobowy, śmieciarka, pojazd ciężarowy z naczepą, ciągnik rolniczy, autobus; natężenie — np. liczba pojazdów na dobę.",
          },
        ],
      },
      {
        code: "oplata",
        title: "Opłata skarbowa",
        fields: [
          {
            code: "kwotaOplaty",
            type: "fee",
            label: "Opłata skarbowa do wniesienia",
            feeAmount: 82,
            feeWaivedWhen: RESIDENTIAL,
            hint: "82 zł za zezwolenie — dołącz potwierdzenie przelewu na rachunek urzędu. Zjazd służący budownictwu mieszkaniowemu jest zwolniony z opłaty (art. 2 ust. 1 pkt 2 ustawy o opłacie skarbowej).",
          },
        ],
      },
      {
        code: "informacje",
        title: "Po wydaniu zezwolenia",
        fields: [
          feeInfo(
            "infoKolejneKroki",
            "Zezwolenie określa miejsce i parametry techniczne zjazdu. Przed rozpoczęciem budowy zjazdu trzeba dokonać czynności wymaganych Prawem budowlanym (zgłoszenie albo pozwolenie na budowę), uzyskać zezwolenie zarządcy drogi na prowadzenie robót w pasie drogowym oraz — jeśli jest wymagany — uzgodnić z zarządcą projekt zagospodarowania działki lub terenu i projekt architektoniczno-budowlany zjazdu (art. 29 ust. 3). Zezwolenie na lokalizację zjazdu wygasa, jeżeli zjazd nie zostanie wybudowany w ciągu 3 lat (art. 29 ust. 5). Zarządca drogi może zażądać uzupełnienia wniosku, np. o pomiary natężenia ruchu albo projekt zmiany organizacji ruchu.",
          ),
        ],
      },
      replySection("Sposób odbioru decyzji"),
    ],
    attachments: [
      {
        code: "mapa",
        label:
          "Kopia aktualnej mapy zasadniczej lub sytuacyjno-wysokościowej w skali 1:500 lub 1:1000 z zaznaczonym proponowanym miejscem lokalizacji zjazdu (albo zjazdem do przebudowy)",
        hint: "Mapa ma obejmować nieruchomość w nawiązaniu do drogi i pokazywać jej granice.",
        required: true,
      },
      {
        code: "tytulPrawnyDokument",
        label: "Dokument potwierdzający tytuł prawny do nieruchomości (np. odpis z księgi wieczystej, wypis z rejestru gruntów, akt notarialny, umowa dzierżawy)",
        hint: "Wymagany, gdy we wniosku nie podano numeru księgi wieczystej.",
        requiredWhen: { not: { field: "numerKw", filled: true } },
      },
      ...roadRegisterExtract,
      {
        code: "wzLubMpzp",
        label: "Decyzja o warunkach zabudowy albo wypis z miejscowego planu zagospodarowania przestrzennego",
        hint: "Nieobowiązkowo — niektórzy zarządcy dróg wymagają jej, gdy zjazd ma obsługiwać nową zabudowę albo zmienia się sposób wykorzystania nieruchomości.",
      },
      feeProofAttachment("Dowód zapłaty opłaty skarbowej (82 zł)", { visibleWhen: OTHER_PURPOSE }),
      ...proxyAttachments(),
      otherAttachments(),
    ],
  };
};
